// Full ratings history: /me/ratings (owner) and /user/:username/ratings.
// Sorting and pagination are done by the API (keyset cursor); this page only
// asks for the next page. Changing the sort starts over, and a late response
// for an older sort is ignored. Ratings are edited on the movie page.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TitleRatingService } from '../../services/titleRatingService';
import RatedMovieCard from '../../components/RatedMovies/RatedMovieCard';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import listStyles from '../../components/RatedMovies/RatedMovies.module.css';
import styles from './Ratings.module.css';

export const RATING_SORTS = [
    { value: 'recent', label: 'Recently Rated' },
    { value: 'highest', label: 'Highest Rated' },
    { value: 'lowest', label: 'Lowest Rated' },
];
const PAGE_SIZE = 20;

export default function Ratings() {
    const { username } = useParams();
    const { user } = useAuth();
    const isOwnerRoute = !username;
    const isOwner = isOwnerRoute || (user?.username && user.username === username);
    const profileType = isOwner ? 'own' : 'other';

    const [sort, setSort] = useState('recent');
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | error | private
    const [loadingMore, setLoadingMore] = useState(false);
    const [moreError, setMoreError] = useState(false);
    const [owner, setOwner] = useState(null); // { username, displayName } for another user's page
    const requestRef = useRef(0);
    const viewedRef = useRef(false);
    const loadingMoreRef = useRef(false); // two quick clicks must not fetch a page twice

    const fetchPage = useCallback((cursor) => (isOwnerRoute
        ? TitleRatingService.myHistory({ sort, limit: PAGE_SIZE, cursor })
        : TitleRatingService.userHistory(username, { sort, limit: PAGE_SIZE, cursor })), [isOwnerRoute, username, sort]);

    const loadFirst = useCallback(async () => {
        const id = ++requestRef.current;
        setStatus('loading');
        setItems([]);
        setNextCursor(null);
        setMoreError(false);
        try {
            const page = await fetchPage(null);
            if (id !== requestRef.current) return; // a newer sort / user won
            if (page?.user) setOwner(page.user);
            if (page?.isPrivate) {
                setStatus('private');
                return;
            }
            setItems(page?.items ?? []);
            setNextCursor(page?.nextCursor ?? null);
            setStatus('ready');
        } catch {
            if (id === requestRef.current) setStatus('error');
        }
    }, [fetchPage]);

    useEffect(() => { loadFirst(); }, [loadFirst]);

    useEffect(() => {
        if (viewedRef.current) return;
        viewedRef.current = true;
        trackEvent(ANALYTICS_EVENTS.RATINGS_HISTORY_VIEWED, { source_screen: 'ratings_history', profile_type: profileType, logged_in: !!user });
    }, [profileType, user]);

    const loadMore = async () => {
        if (!nextCursor || loadingMoreRef.current) return;
        const id = requestRef.current;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setMoreError(false);
        try {
            const page = await fetchPage(nextCursor);
            if (id !== requestRef.current) return;
            if (page?.isPrivate) {
                // Visibility changed mid-session: drop what we had.
                setItems([]);
                setNextCursor(null);
                setStatus('private');
                return;
            }
            setItems((prev) => {
                const seen = new Set(prev.map((i) => i.tmdbId));
                return [...prev, ...(page?.items ?? []).filter((i) => !seen.has(i.tmdbId))];
            });
            setNextCursor(page?.nextCursor ?? null);
        } catch {
            if (id === requestRef.current) setMoreError(true);
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    };

    const changeSort = (value) => {
        if (value === sort) return;
        setSort(value);
        trackEvent(ANALYTICS_EVENTS.RATINGS_SORT_CHANGED, { source_screen: 'ratings_history', profile_type: profileType, sort_order: value, logged_in: !!user });
    };

    const name = owner?.displayName || owner?.username || username;
    const title = isOwner ? 'My Ratings' : `${name}'s Ratings`;
    const backTo = isOwnerRoute ? '/me' : `/user/${encodeURIComponent(username)}`;

    useEffect(() => { document.title = `${title} | Aluvo`; }, [title]);

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <Link to={backTo} className={styles.backLink}>← Back to profile</Link>
                <div className={styles.header}>
                    <h1 className={styles.title}>{title}</h1>
                    {status !== 'private' && (
                        <label className={styles.sortLabel}>
                            <span>Sort</span>
                            <select value={sort} onChange={(e) => changeSort(e.target.value)} className={styles.sortSelect} aria-label="Sort ratings">
                                {RATING_SORTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </label>
                    )}
                </div>

                {status === 'loading' && <p className={styles.state} role="status">Loading ratings…</p>}

                {status === 'error' && (
                    <p className={styles.state} role="alert">
                        Couldn't load ratings.{' '}
                        <button type="button" className={listStyles.linkBtn} onClick={loadFirst}>Try again</button>
                    </p>
                )}

                {status === 'private' && (
                    <div className={styles.state}>
                        <p className={styles.stateTitle}>🔒 This profile is private</p>
                        <p>Ratings are only visible to people this profile is shared with.</p>
                    </div>
                )}

                {status === 'ready' && items.length === 0 && (
                    isOwner ? (
                        <div className={styles.state}>
                            <p className={styles.stateTitle}>Your movie opinions start here.</p>
                            <p>Rate movies you've watched and they'll appear here.</p>
                            <Link to="/discover" className={styles.cta}>Explore Movies</Link>
                        </div>
                    ) : (
                        <p className={styles.state}>No ratings yet.</p>
                    )
                )}

                {status === 'ready' && items.length > 0 && (
                    <>
                        <ul className={listStyles.list} aria-label={title}>
                            {items.map((item) => <RatedMovieCard key={item.tmdbId} item={item} variant="row" />)}
                        </ul>
                        {moreError && <p className={styles.state} role="alert">Couldn't load more ratings.</p>}
                        {nextCursor && (
                            <button type="button" className={styles.loadMore} onClick={loadMore} disabled={loadingMore}>
                                {loadingMore ? 'Loading…' : moreError ? 'Try again' : 'Load More'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
